<template>
  <Modal
    v-if="isVisible"
    :show="isVisible"
    :title="t('Estados de') + ' ' + paisName"
    :close-label="t('Fechar')"
    elem-id="modalLocationStates"
    @close="close"
    @dismiss="close"
    >
      

    <div>
      <p>{{ t('Selecione os estados para adicionar à franquia') }}:</p>

      <!-- Search filter -->
      <div class="form-group">
        <label for="searchState">{{ t('Procurar') }}:</label>
        <input-text
          data-testid="franquias-locations-states-search"
          elem-id="franquias_statesSearchFilter"
          id="searchState"
          v-model="searchFilter"
          :placeholder="t('Digite para filtrar')"
          style="width: 100%;" />
      </div>

      <!-- Loading state -->
      <inline-message v-if="isLoading" severity="info" :closable="false">
        {{ t('Carregando estados') }}...
      </inline-message>

      <!-- Estados list -->
      <table v-else class="table table-hover table-striped table-bordered" style="max-height: 400px; overflow-y: auto;">
        <tbody>
          <tr v-for="estado in filteredEstados" :key="estado.ID">
            <td style="width: 50%">
              <i v-if="isAlreadyAdded(estado.ID)" class="pi pi-check"></i>
              {{ estado.Name }}
            </td>
            <td style="width: 25%">
              <Button
                :data-testid="`franquias-locations-add-state-${estado.ID}`"
                icon="pi pi-plus"
                :label="t('Adicionar estado')"
                size="small"
                severity="secondary"
                @click="handleAddState(estado.ID)"
                :disabled="isAlreadyAdded(estado.ID)" />
            </td>
            <td style="width: 25%">
              <Button
                :data-testid="`franquias-locations-see-cities-${estado.ID}`"
                icon="pi pi-eye"
                :label="t('Ver cidades')"
                size="small"
                severity="info"
                @click="handleSeeCities(estado.ID, estado.Name)" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLocalidades } from '../../composables/useLocalidades';
import { eventBus } from '../../eventBus';

type Id = number | string;

interface EstadoItem {
  ID?: Id;
  Name?: string;
  [key: string]: unknown;
}

interface FranquiaData {
  AreasAtuacao?: {
    States?: Array<{ ID?: Id }>;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface ModalOpenPayload {
  franquiaId?: Id;
  franquiaData?: FranquiaData;
  paisId?: Id;
  paisName?: string;
}

interface Props {
  franquiaId?: number | null;
  franquiaData?: FranquiaData | null;
}

const props = withDefaults(defineProps<Props>(), {
  franquiaId: null,
  franquiaData: null,
});

const { t } = useI18n();
const { fetchEstados, toggleRelacao } = useLocalidades();

// Reactive data
const isVisible = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const estados = ref<EstadoItem[]>([]);
const searchFilter = ref<string>('');
const currentPaisId = ref<Id | null>(null);
const paisName = ref<string>('');
const currentFranquiaId = ref<Id | null>(null);
const currentFranquiaData = ref<FranquiaData | null>(null);

// Computed
const filteredEstados = computed<EstadoItem[]>(() => {
  if (!searchFilter.value) {
    return estados.value;
  }
  const filter = searchFilter.value.toLowerCase();
  return estados.value.filter((estado) =>
    (estado.Name || '').toLowerCase().includes(filter),
  );
});

const loadEstados = async (): Promise<void> => {
  if (!currentPaisId.value) {
    return;
  }

  isLoading.value = true;
  try {
    estados.value = (await fetchEstados(Number(currentPaisId.value))) as EstadoItem[];
  } catch {
    eventBus.emit('globalMessage.error', t('Erro ao carregar estados'));
  } finally {
    isLoading.value = false;
  }
};

// Methods
const open = async (paisId: Id, paisNameValue: string): Promise<void> => {
  isVisible.value = true;
  currentPaisId.value = paisId;
  paisName.value = paisNameValue;
  searchFilter.value = '';
  await loadEstados();
};

const close = (): void => {
  isVisible.value = false;
  searchFilter.value = '';
  estados.value = [];
  currentPaisId.value = null;
  paisName.value = '';
};

// Event handlers for centralized ModalManager
const handleModalOpen = (data?: ModalOpenPayload): void => {
  if (data?.franquiaId) {
    currentFranquiaId.value = data.franquiaId;
  }
  if (data?.franquiaData) {
    currentFranquiaData.value = data.franquiaData;
  }
  if (data?.paisId && data?.paisName) {
    void open(data.paisId, data.paisName);
  }
};

const isAlreadyAdded = (estadoId: Id | undefined): boolean => {
  const franquiaData = currentFranquiaData.value || props.franquiaData;
  if (!franquiaData || !franquiaData.AreasAtuacao) {
    return false;
  }
  const states = franquiaData.AreasAtuacao.States || [];
  return states.some((state) => state.ID === estadoId);
};

const handleAddState = async (estadoId: Id | undefined): Promise<void> => {
  const franquiaId = currentFranquiaId.value || props.franquiaId;
  try {
    // NOTE: payload uses PascalCase keys preserved from the legacy JS code;
    // see FranquiasLocationsTab.vue for the documented latent contract mismatch.
    const relacaoData = {
      Type: 'state',
      LocalidadeID: estadoId,
      OwnerID: franquiaId,
      OwnerType: 'franquia',
    };

    await toggleRelacao(relacaoData as unknown as Parameters<typeof toggleRelacao>[0]);

    eventBus.emit('globalMessage.success', t('Estado adicionado com sucesso'));

    // Emit via eventBus so FranquiasLocationsTab can refresh permissions
    eventBus.emit('location-added', { franquiaId: franquiaId });
    close();
  } catch {
    eventBus.emit('globalMessage.error', t('Erro ao adicionar estado'));
  }
};

const handleSeeCities = (estadoId: Id | undefined, estadoName: string | undefined): void => {
  // Emit event via eventBus to open cities modal (centralized modal pattern)
  eventBus.emit('modal.locations-cities.open', {
    estadoId: estadoId,
    estadoName: estadoName,
    franquiaId: currentFranquiaId.value,
    franquiaData: currentFranquiaData.value,
  });

  // Close this modal
  close();
};

// Lifecycle hooks for centralized ModalManager
onMounted(() => {
  eventBus.on('modal.locations-states.open', handleModalOpen);
  eventBus.on('modal.locations-states.close', close);
});

onBeforeUnmount(() => {
  eventBus.off('modal.locations-states.open', handleModalOpen);
  eventBus.off('modal.locations-states.close', close);
});

// Expose methods for parent
defineExpose({
  open,
  close,
});
</script>

<style scoped>
.form-group {
  margin-bottom: 15px;
}

table {
  display: block;
  max-height: 400px;
  overflow-y: auto;
}

/* PrimeVue components handle their own styling */
</style>

